import type { ComposerTranslation } from 'vue-i18n';
import {
    type ContainerElement,
    type DataField,
    ElementType,
    type FormData,
    type FormElement,
    type FormFillContext,
    type NumberInput,
    type TextInput,
    type VisibilityRule,
    VisibilityRuleType
} from './types';
import { FormElementsConfigMap } from './constants';
import { PropertyFormat, type ExpandedSchemaProperty } from 'vince-core/types/tables';
import { shortId } from 'vince-ui/utils/string';
import { Comparison, DataType } from 'vince-core/types/common';
import { JsonSchemaType } from 'vince-core/types/formats';

/**
 * TODO all comparison should be generalized into its own module and rewritten to remove most possible ifs
 */
const compareStringValue = (value: string, refValue: string, comparison: Comparison): boolean => {
    const v = value || '';

    if (comparison === Comparison.isBlank) {
        return v.length === 0;
    } else if (comparison === Comparison.isNotBlank) {
        return v.length > 0;
    } else if (comparison === Comparison.equal) {
        return v === refValue;
    } else if (comparison === Comparison.notEqual) {
        return v !== refValue;
    } else if (comparison === Comparison.stringContains) {
        return v.includes(refValue);
    } else if (comparison === Comparison.stringNotContains) {
        return !v.includes(refValue);
    } else if (comparison === Comparison.greaterThan) {
        return v > refValue;
    } else if (comparison === Comparison.greaterThanInclusive) {
        return v >= refValue;
    } else if (comparison === Comparison.lessThan) {
        return v < refValue;
    } else if (comparison === Comparison.lessThanInclusive) {
        return v <= refValue;
    }

    return true;
};

const compareDateValue = (_value: string, _refValue: string, _comparison: Comparison): boolean => {
    // Not implemented
    return true;
};

const compareNumberValue = (value: string, refValue: string, comparison: Comparison): boolean => {
    const rv = parseFloat(refValue);
    // Rule is incorrect
    if (isNaN(rv)) { return true; }

    // Value is incorrect
    const v = parseFloat(value);
    if (isNaN(v)) { return false; }

    if (comparison === Comparison.greaterThan) {
        return v > rv;
    } else if (comparison === Comparison.greaterThanInclusive) {
        return v >= rv;
    } else if (comparison === Comparison.lessThan) {
        return v < rv;
    } else if (comparison === Comparison.lessThanInclusive) {
        return v <= rv;
    } else if (comparison === Comparison.equal) {
        return v === rv;
    } else if (comparison === Comparison.notEqual) {
        return v !== rv;
    }
    // Rule is incorrect
    return true;
};

const checkFormDataRule = (rule: VisibilityRule, formData: Record<string, unknown>, formDataTypes: Record<string, DataType>): boolean => {
    if (!Reflect.has(formData, rule.field)) {
        return false;
    }

    const dataType = formDataTypes[rule.field]!;
    const value = formData[rule.field]! as string;

    if (dataType === DataType.NUMERIC) {
        return compareNumberValue(value, rule.value, rule.comparison);
    } else if (dataType === DataType.DATE) {
        return compareDateValue(value, rule.value, rule.comparison);
    } else if (dataType === DataType.STRING) {
        return compareStringValue(value, rule.value, rule.comparison);
    }

    // not implemented
    return true;
};

const checkContextRule = (rule: VisibilityRule, context: FormFillContext): boolean => {
    if (['nickname', 'lastName', 'firstName', 'email'].includes(rule.field)) {
        return compareStringValue(context[rule.field as (keyof FormFillContext)] as string, rule.value, rule.comparison);
    }

    if (rule.field === 'groups') {
        const contains = context.groups.includes(rule.value);

        if (rule.comparison === Comparison.contains) {
            return contains;
        } else if (rule.comparison === Comparison.notContains) {
            return !contains;
        }
    } else if (rule.field === 'tags') {
        const contains = Object.keys(context.tags).filter(t => context.tags[t] !== null).includes(rule.value);

        if (rule.comparison === Comparison.contains) {
            return contains;
        } else if (rule.comparison === Comparison.notContains) {
            return !contains;
        }
    }

    return true;
};

export const checkRule = (rule: VisibilityRule, formData: Record<string, unknown>, formDataTypes: Record<string, DataType>, context: FormFillContext): boolean => {
    return rule.type === VisibilityRuleType.CONTEXT ? checkContextRule(rule, context) : checkFormDataRule(rule, formData, formDataTypes);
};

export const schemaTypeToDataType = (type: JsonSchemaType, format?: PropertyFormat): DataType => {
    // date
    if (format === PropertyFormat.ISO_DATE_TIME) {
        return DataType.DATE;
    }

    if (type === JsonSchemaType.STRING) {
        return DataType.STRING;
    } else if (type === JsonSchemaType.NUMBER || type === JsonSchemaType.INTEGER) {
        return DataType.NUMERIC;
    }

    // not supported
    return DataType.STRING;
};

export const tableColumnsToDataFields = (cols: ExpandedSchemaProperty[]): DataField[] => {
    return cols.map((it) => {
        return {
            id: shortId(),
            sourceField: it.name,
            sourceFieldLabel: it.title || it.name,
            sourceFieldType: schemaTypeToDataType(it.type, it.format)
        };
    });
};

export const dataFieldsToElements = (fields: DataField[], container: ContainerElement, t: ComposerTranslation): FormElement[] => {
    return fields.map((it:DataField) => {
        if (it.sourceFieldType === DataType.STRING) {
            const el = FormElementsConfigMap[ElementType.TEXT_INPUT].create(container, t) as TextInput;
            el.dataFieldId = it.id;
            el.label = it.sourceFieldLabel;
            return el;
        } else if (it.sourceFieldType === DataType.NUMERIC) {
            const el = FormElementsConfigMap[ElementType.NUMBER_INPUT].create(container, t) as NumberInput;
            el.dataFieldId = it.id;
            el.label = it.sourceFieldLabel;
            return el;
        } else if (it.sourceFieldType === DataType.DATE) {
            const el = FormElementsConfigMap[ElementType.DATE_INPUT].create(container, t) as NumberInput;
            el.dataFieldId = it.id;
            el.label = it.sourceFieldLabel;
            return el;
        }

        return undefined;
    }).filter(it => it !== undefined);
};

/**
 * Extracted to separate method to later support embedded objects
 */
const getValue = (data: unknown, sourceField: string): unknown => {
    if (Reflect.has(data as object, sourceField)) {
        return Reflect.get(data as object, sourceField);
    } else {
        return undefined;
    }
};

function generatePreviewValue (f: DataField) {
    if (f.sourceFieldType === DataType.STRING) {
        return '';
    } else if (f.sourceFieldType === DataType.NUMERIC) {
        return 0;
    } else if (f.sourceFieldType === DataType.DATE) {
        return new Date().toISOString();
    } else {
        return undefined;
    }
}

export const prepareFormData = (formData: FormData, data: unknown, forPreview: boolean): Record<string, unknown> => {
    const result = {} as Record<string, unknown>;

    const fields = formData?.fields || [];

    for (const f of fields) {
        result[f.id] = forPreview ? generatePreviewValue(f) : getValue(data, f.sourceField);
    }
    return result;
};
